import { ReactNode } from 'react';
import Modal from 'react-modal';
import classNames from 'classnames';
import { IconClose } from 'components/Icons';
import { prefixClass } from 'lib/utils';
import useIsMobile from 'hooks/useIsMobile';

import './CreateModal.scss';

const prefix = prefixClass('create-modal');

export interface CreateModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal subtitle (typically location name) */
  subtitle?: string;
  /** Icon displayed on the left side */
  icon: ReactNode;
  /** Form content - the unique fields for each modal type */
  children: ReactNode;
  /** Footer content - typically CMComposer or action buttons */
  footer?: ReactNode;
  /** Whether the modal is in internal mode (shows blue outline) */
  isInternal?: boolean;
  /** Additional class name for the modal */
  className?: string;
}

/**
 * CreateModal - Shared modal shell for creating requests, emails, groups, etc.
 *
 * Provides consistent layout:
 * - Icon on the left
 * - Title and subtitle header
 * - Form content area (children)
 * - Footer area (typically CMComposer)
 * - Close button
 *
 * Usage:
 * ```tsx
 * <CreateModal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="New Ask Request"
 *   subtitle={locationName}
 *   icon={<IconTaskNew />}
 *   footer={<CMComposer ... />}
 * >
 *   <CreateModal.FormRow label="Title">
 *     <KSInputField ... />
 *   </CreateModal.FormRow>
 * </CreateModal>
 * ```
 */
export const CreateModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  isInternal = false,
  className,
}: CreateModalProps) => {
  const isMobile = useIsMobile();

  if (!isOpen) {
    return null;
  }

  const content = (
    <div
      className={classNames(prefix('__wrapper'), {
        [prefix('__wrapper--mobile')]: isMobile,
        [prefix('__wrapper--internal')]: isInternal,
      })}
    >
      <div className={prefix('__icon')}>{icon}</div>

      <div className={prefix('__content-area')}>
        <div className={prefix('__header')}>
          <h2 className={prefix('__title')}>{title}</h2>
          {subtitle && <p className={prefix('__subtitle')}>{subtitle}</p>}
        </div>

        <div className={prefix('__content')}>
          <div className={prefix('__form')}>{children}</div>
          {footer && <div className={prefix('__footer')}>{footer}</div>}
        </div>
      </div>

      <button className={prefix('__close')} type="button" onClick={onClose} aria-label="Close">
        <IconClose />
      </button>
    </div>
  );

  if (isMobile) {
    return <div className={classNames(prefix(), prefix('--mobile'), className)}>{content}</div>;
  }

  return (
    <Modal
      ariaHideApp={false}
      onRequestClose={onClose}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      className={classNames(prefix(), className)}
    >
      {content}
    </Modal>
  );
};

/** Form row with label */
interface FormRowProps {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  error?: string;
}

const FormRow = ({ label, htmlFor, children, error }: FormRowProps) => (
  <div className={classNames(prefix('__form-row'), { [prefix('__form-row--error')]: !!error })}>
    <label htmlFor={htmlFor} className={prefix('__form-label')}>
      {label}
    </label>
    <div className={prefix('__form-input')}>
      {children}
      {error && <span className={prefix('__form-error')}>{error}</span>}
    </div>
  </div>
);

/** Form frame for standalone elements like checkboxes */
interface FormFrameProps {
  children: ReactNode;
}

const FormFrame = ({ children }: FormFrameProps) => <div className={prefix('__form-frame')}>{children}</div>;

/** Actions container for button-based footers (alternative to CMComposer) */
interface ActionsProps {
  children: ReactNode;
}

const Actions = ({ children }: ActionsProps) => <div className={prefix('__actions')}>{children}</div>;

// Attach sub-components
CreateModal.FormRow = FormRow;
CreateModal.FormFrame = FormFrame;
CreateModal.Actions = Actions;

export default CreateModal;
